import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { IconButton, Tooltip } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import CloseIcon from "@mui/icons-material/Close";

import hostCapture, { CAPTURE_STATE } from "../liveTap/hostCapture/hostCapture";
import useHostCapture from "../hooks/useHostCapture";
import CaptureBar, { ICON_BTN_SX, STOP_BTN_SX } from "./CaptureBar";
import StopCaptureDialog from "./StopCaptureDialog";

// The host-audio capture bar (TT-1102): shown on the Live Tap page while THIS
// tab is the capture agent for the doc — source, level, elapsed, mute, stop.
// Stopping ends the tapestry (close drip), so it asks once.

function stateLabel(state) {
  switch (state) {
    case CAPTURE_STATE.STARTING:
      return "Starting";
    case CAPTURE_STATE.RECONNECTING:
      return "Reconnecting";
    case CAPTURE_STATE.STOPPING:
      return "Stopping";
    case CAPTURE_STATE.STOPPED:
      return "Ended";
    case CAPTURE_STATE.ERROR:
      return "Failed";
    default:
      return "Live";
  }
}

const HostCaptureBar = ({ docId }) => {
  const capture = useHostCapture();
  const [elapsed, setElapsed] = useState(hostCapture.elapsedSeconds());
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!capture.active) return undefined;
    const tick = setInterval(() => setElapsed(hostCapture.elapsedSeconds()), 1000);
    return () => clearInterval(tick);
  }, [capture.active]);

  const stop = useCallback(async () => {
    setConfirmOpen(false);
    await hostCapture.stop();
  }, []);

  if (capture.docId !== docId || capture.dismissed) return null;
  const ended = capture.state === CAPTURE_STATE.STOPPED || capture.state === CAPTURE_STATE.ERROR;
  if (capture.state === CAPTURE_STATE.IDLE) return null;

  const running = capture.state === CAPTURE_STATE.RUNNING;

  return (
    <CaptureBar
      label={stateLabel(capture.state)}
      quiet={!running || capture.muted}
      source={capture.sourceLabel}
      level={ended ? null : capture.muted ? 0 : capture.level}
      elapsed={elapsed}
      lastText={ended ? null : capture.lastText}
      error={capture.error}
    >
      {!ended && (
        <Tooltip title={capture.muted ? "Unmute" : "Mute"}>
          <IconButton size="small" sx={ICON_BTN_SX} onClick={() => hostCapture.setMuted(!capture.muted)}>
            {capture.muted ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )}
      {!ended && (
        <Tooltip title="Stop and end this tapestry">
          <span>
            <IconButton
              size="small"
              sx={STOP_BTN_SX}
              onClick={() => setConfirmOpen(true)}
              disabled={capture.state === CAPTURE_STATE.STOPPING}
            >
              <StopCircleIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {ended && (
        <IconButton size="small" sx={ICON_BTN_SX} onClick={() => hostCapture.dismiss()} aria-label="Dismiss">
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
      <StopCaptureDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} onStop={stop} />
    </CaptureBar>
  );
};

HostCaptureBar.propTypes = {
  docId: PropTypes.string,
};

export default HostCaptureBar;
